import {
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getWeek,
  isWeekend,
  Locale,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ViewMode } from '../models/preferences';
import { splitFormatIntoSections } from '@mui/x-date-pickers/internals';
import { debug } from 'console';

interface WeekOptions {
  locale?: Locale;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  firstWeekContainsDate: 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

export function extractDate(dateString: string) {
  // Use a regular expression to match the desired date format
  const regex = /([A-Za-z]{3}\s\d{1,2}\s\d{4})/;
  const match = dateString.match(regex);

  // If a match is found, return the matched date
  return match ? match[0] : null;
}

export function formatDateOnly(dateString: string): string {
  // Check if the date string already contains a time component
  let fullDateString;

  const regexDate = /\d{1,2}\/\d{1,2}\/\d{4}/g;
  const regexDotDate = /\d{1,2}\.\d{1,2}\.\d{4}/g; // For "07.08.2025" format

  if (dateString.includes('GMT')) {
    //fullDateString = dateString;
    fullDateString = extractDate(dateString);
  } else if (dateString.match(regexDotDate)) {
    // Handle "07.08.2025" format (dd.mm.yyyy)
    let datePart = dateString;

    // If it includes 'T', extract just the date part
    if (dateString.includes('T')) {
      datePart = dateString.split('T')[0];
    }

    let split = datePart.split('.');
    if (split.length !== 3) {
      throw new Error("Invalid dot-separated date string provided");
    }

    // Assuming day.month.year format
    const day = parseInt(split[0]);
    const month = parseInt(split[1]) - 1; // JS months are 0-based
    const year = parseInt(split[2]);

    // Create a date object and format it consistently
    fullDateString = new Date(year, month, day, 0, 0, 0);
  } else if (dateString.match(regexDate)) {
    // mm/dd/yyyy => year,month,date
    //TODO: This will not work in countries (Brazil) where the date format is dd/mm/yyyy
    let split = dateString.split('/');
    if (split.length !== 3) {
      throw new Error("Invalid date string provided");
    }
    fullDateString = new Date(parseInt(split[2]), parseInt(split[0]) - 1, parseInt(split[1]), 0, 0, 0);
  } else if (dateString.includes('T')) {
    // Split the string at 'T' and take the first part (the date)
    const datePart = dateString.split('T')[0];
    // Append 'T00:00:00' to the date part
    fullDateString = datePart + 'T00:00:00';
  } else {
    // Otherwise, append 'T00:00:00' for midnight
    fullDateString = dateString + 'T00:00:00';
  }

  // Create a Date object from the modified string
  const dateObject = new Date(fullDateString!);

  // Check for invalid date
  if (isNaN(dateObject.getTime())) {
    return '';
  }

  // Define formatting options with exact string literals
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  // Format the date as needed
  const formattedDate = dateObject.toLocaleDateString("en-US", options);

  return formattedDate; // Adjust the return format as needed
}

export const weekOptions: WeekOptions = {
  weekStartsOn: 1,
  firstWeekContainsDate: 7,
};

export function getFormatDateDayForDesktop(date: Date): string {
  return `${format(date, 'EEEE')}, ${format(date, 'MMMM')} ${format(
    date,
    'd'
  )}`;
}

export function getFormatDateDayForMobile(date: Date): string {
  return `${format(date, 'EEEE')}, ${format(date, 'MMM')} ${format(date, 'd')}`;
}

export function getFormatDateWeek(date: Date): string {
  return `${format(date, 'LLLL')}, week ${format(date, 'I')}`;
}

export function getFormatDateMonth(date: Date): string {
  return `${format(date, 'LLLL')} ${format(date, 'yyyy')}`;
}

export function getPreviousDay(date: Date): Date {
  const pastDate = new Date(date);
  pastDate.setDate(pastDate.getDate() - 1);

  return pastDate;
}

export function getNextDay(date: Date): Date {
  const futureDate = new Date(date);
  futureDate.setDate(futureDate.getDate() + 1);

  return futureDate;
}

export function getPreviousMonth(date: Date): Date {
  const pastMonth = new Date(date);
  pastMonth.setMonth(pastMonth.getMonth() - 1);

  return pastMonth;
}

export function getNextMonth(date: Date): Date {
  const pastMonth = new Date(date);
  pastMonth.setMonth(pastMonth.getMonth() + 1);

  return pastMonth;
}

export function getPreviousWeek(date: Date): Date {
  const previousWeek = new Date(date);
  previousWeek.setDate(previousWeek.getDate() - 7);

  return previousWeek;
}

export function getNextWeek(date: Date): Date {
  const nextWeek = new Date(date);
  nextWeek.setDate(nextWeek.getDate() + 7);

  return nextWeek;
}

export function getFriday(date: Date): Date {
  const lastDayOfWeek = endOfWeek(date, weekOptions);
  const friday = new Date(lastDayOfWeek);
  friday.setDate(friday.getDate() - 2);

  return friday;
}

export function getStartDateAccording(viewMode: ViewMode, date: Date): Date {
  switch (viewMode) {
    case ViewMode.DAY:
      return startOfDay(date);
    case ViewMode.OLD_VIEW:
    case ViewMode.MONTH:
      return startOfMonth(date);
    default:
      return startOfWeek(date, weekOptions);
  }
}

export function getEndDateAccording(viewMode: ViewMode, date: Date): Date {
  switch (viewMode) {
    case ViewMode.DAY:
      return endOfDay(date);
    case ViewMode.WEEK:
      return endOfWeek(date, weekOptions);
    case ViewMode.WORK_WEEK:
      return getFriday(date);
    case ViewMode.OLD_VIEW:
    case ViewMode.MONTH:
      return endOfMonth(date);
    default:
      return endOfMonth(date);
  }
}

export const startAndEndOfWorkWeek = (
  isWorkWeek: boolean,
  date: Date
): { startWeek: Date; endWeek: Date } => {
  const startWeek = startOfWeek(date, weekOptions);
  const endWeek = isWorkWeek ? getFriday(date) : endOfWeek(date, weekOptions);

  return { startWeek, endWeek };
};

export function getIntervalDayForWeekAccording(
  isWorkWeek: boolean,
  date: Date
): string[] {
  const { startWeek, endWeek } = startAndEndOfWorkWeek(isWorkWeek, date);

  return eachDayOfInterval({
    start: startWeek,
    end: endWeek,
  }).map((dateFromInterval) => dateFromInterval.toString());
}

export const startAndEndOfWorkMonth = (
  date: Date
): { startDay: Date; endDay: Date } => {
  const startDay = startOfMonth(date);
  const endDay = endOfMonth(date);

  return { startDay, endDay };
};

export function getIntervalDayMonthAccording(date: Date): string[] {
  let { startDay, endDay } = startAndEndOfWorkMonth(date);

  if (format(startDay, 'eee') != 'Mon') {
    startDay = startOfWeek(startDay, weekOptions);
  }

  if (format(endDay, 'eee') !== 'Sun') {
    endDay = endOfWeek(endDay, weekOptions);
  }

  const month = eachDayOfInterval({
    start: startDay,
    end: endDay,
  });

  return month.map((day) => day.toString());
}

export function getWeeksNumberInMonth(date: Date): number[] {
  const startDayOfMonth = startOfMonth(date);
  const endDayOfMonth = endOfMonth(date);

  let firstWeekNumber = getWeek(startDayOfMonth, weekOptions);
  const lastWeekNumber = getWeek(endDayOfMonth, weekOptions);

  const result: number[] = [];

  // if january cast week from precedent year
  if (firstWeekNumber > lastWeekNumber) {
    result.push(firstWeekNumber);
    firstWeekNumber = 1;
  }

  for (
    let weekNumber = firstWeekNumber;
    weekNumber <= lastWeekNumber;
    weekNumber++
  ) {
    result.push(weekNumber);
  }

  return result;
}

export function getDaysMonth(date: Date): Date[] {
  return eachDayOfInterval({
    start: startOfMonth(date),
    end: endOfMonth(date),
  });
}

export function getDaysWeek(date: Date): Date[] {
  return eachDayOfInterval({
    start: startOfWeek(date),
    end: endOfWeek(date),
  });
}

const SEVEN_DAYS = 7;

export function getDateByWeekNumber(weekNumber: number, date: Date): Date {
  const listOfWeeksInMonth = getWeeksNumberInMonth(date);
  const startOfMonthDate = startOfMonth(date);

  listOfWeeksInMonth.forEach((weekNumberInMonth, indexOfWeek) => {
    if (weekNumberInMonth === weekNumber) {
      startOfMonthDate.setDate(
        startOfMonthDate.getDate() + SEVEN_DAYS * indexOfWeek
      );
    }
  });

  return startOfWeek(startOfMonthDate, weekOptions);
}

export function getFirstDayPrevMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1, 12, 0, 0);
}
export function getLastDayPrevMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 0, 12, 0, 0);
}

export function generateIntervalDates(
  startDateStr: string,
  endDateStr: string
): Date[] {
  const dateArray = eachDayOfInterval({
    start: new Date(formatDateOnly(startDateStr)),
    end: new Date(formatDateOnly(endDateStr)),
  });

  const filteredDates = dateArray.filter((date) => !isWeekend(date));

  return filteredDates;
}

export function formatTableViewDate(inputDate: string): string {
  const date = parseISO(inputDate);
  const formattedDate = format(date, 'EEEE, MMMM d');

  return formattedDate;
}

export function formatDisplayReportsDate(inputDate: string): string {
  const date = parseISO(inputDate);
  const formattedDate = format(date, 'dd MMM yyyy');

  return formattedDate;
}

export function formatActivityDate(inputDate: Date): string {
  return format(inputDate, 'yyyy-MM-dd');
}

